/**
 * SMTP port <-> TLS mode, for the two settings panels that ask for both (the
 * mailbox card and each sending channel).
 *
 * This mirrors `resolveSmtpSecurity` in the API's `lib/mail/transport.ts`, which
 * is the one that actually decides how the socket is opened. The copy here is
 * not the enforcement, it is the explanation: the server silently correcting a
 * contradictory setting is right, but leaving the operator staring at a ticked
 * SSL box that the server ignores is not. Keep the two in step.
 */

/** Ports that speak implicit TLS (SMTPS) from the first byte. */
const IMPLICIT_TLS_PORTS = new Set([465]);
/** Ports that always start in the clear and upgrade with STARTTLS. */
const STARTTLS_PORTS = new Set([25, 587, 2525]);

/**
 * What the SSL toggle should read for a port. Unknown ports keep the operator's
 * own answer - a self-hosted relay may run implicit TLS anywhere.
 */
export function smtpSecureForPort(port: number, current: boolean): boolean {
  if (IMPLICIT_TLS_PORTS.has(port)) return true;
  if (STARTTLS_PORTS.has(port)) return false;
  return current;
}

/** Whether the toggle contradicts the port, i.e. the server will override it. */
export function smtpSecurityMismatch(port: number, secure: boolean): boolean {
  return smtpSecureForPort(port, secure) !== secure;
}

/**
 * The line under the SSL toggle. Only speaks when the toggle contradicts the
 * port, because that is the one case where what the panel shows and what the
 * socket does are different things.
 */
export function smtpTlsHint(
  t: (key: string, vars?: Record<string, string | number>) => string,
  port: number,
  secure: boolean,
): string | undefined {
  if (!smtpSecurityMismatch(port, secure)) return undefined;
  const key = smtpSecureForPort(port, secure) ? "settings.mail.smtpTlsImplicit" : "settings.mail.smtpTlsStarttls";
  return t(key, { port });
}
